import { useState, useMemo, useContext, useEffect } from "react";
import EmptyPlansTable from "./EmptyPlansTable";
import TablePagination from "../../shared/TablePagination";
import TableSkeleton from "../../shared/TableSkeleton";
import SectionHeader from "../../shared/SectionHeader";
import ErrorState from "../../shared/ErrorState";
import { AppContext } from "../../../../../context/app";
import { useUserSubscriptionsQuery } from "../../../../../apis/subscriptionApi.hooks";
import {
  flexRender,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  Row,
} from "@tanstack/react-table";
import { Plus } from "lucide-react";
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@pushpress/pushpress-ui";
import { SubscriptionStatus } from "src/apis/metricsApi";
import { PAGINATION_PAGE_SIZE } from "../../../constants";
import { useAppointmentHistoryQuery } from "../../../../../apis/appointmentApi.hooks";
import { GetAppointmentHistoryResult } from "../../../../../apis/appointmentApi";
import { getUserProfileSubscriptionResponse } from "../../../../../apis/subscriptionApi";
import { convertDateStringOrTimestampToUsDateFormat, getPlansDetails, getSubscriptionStatus } from "../../../helpers";


const convertToTableData = (
  subscriptionData: getUserProfileSubscriptionResponse,
  appointmentHistoryData: GetAppointmentHistoryResult,
  currencyIso: string,
): TableRow[] => {
  let subscriptions = [];
  let appointmentCredits = [];

  if (subscriptionData?.data?.length) {
    subscriptions = subscriptionData.data.map((subscription) => {
      const {
        startDate,
        pauseDate,
        status,
        nextCharge,
        discountAmount,
        currentPeriodUsage: { checkins, limit },
        planObject: { name },
        amount,
      } = subscription;
      const limitValue = limit === -1 ? "Unlimited" : limit;
      const usage = `${checkins} / ${limitValue}`;
      const subscriptionStatus = getSubscriptionStatus({ status, pauseDate })
      const details = getPlansDetails({ amount, discountAmount, currencyIso })
      return {
        name,
        startDate: convertDateStringOrTimestampToUsDateFormat(startDate),
        nextCharge: convertDateStringOrTimestampToUsDateFormat(nextCharge),
        usage,
        status: subscriptionStatus,
        details,
        isCredit: false,
      };
    });
  }
  if (appointmentHistoryData?.schedule?.length) {
    appointmentCredits = appointmentHistoryData.schedule
      .filter(
        (appointmentHistory) => appointmentHistory.eventStatus == "NOTBOOKED",
      )
      .reduce((creditsWithCount, apt) => {
        const uuid = apt.appointmentType.uuid;
        const existing = creditsWithCount.find(
          (apt) => apt.appointmentType.uuid === uuid,
        );

        if (existing) {
          existing.count += 1;
        } else {
          creditsWithCount.push({ ...apt, count: 1 });
        }

        return creditsWithCount;
      }, [])
      .map((appointmentHistory) => {
        const {
          appointmentType: { name },
          paidAt,
          expiresAt,
          count,
        } = appointmentHistory;
        const usage = expiresAt
          ? `Expires on ${convertDateStringOrTimestampToUsDateFormat(expiresAt)}`
          : "Does not expire";
        return {
          name,
          startDate: convertDateStringOrTimestampToUsDateFormat(paidAt),
          nextCharge: "—",
          usage,
          status: "active",
          details: `${count} Credit(s) Remaining`,
          isCredit: true,
        };
      });
  }
  return subscriptions.concat(appointmentCredits);
};

const getDefaultFilters = (rows: TableRow[]) => {
  const activeRowCount = rows.filter(
    (row) => row.status === "active",
  ).length;
  if (activeRowCount > 0) {
    return {
      viewMode: "active",
      filter: defaultActiveFilter,
    };
  }
  return {
    viewMode: "all",
    filter: [],
  };
};

const getStatusBadge = ({ row: { original } }): JSX.Element => {
  const { status } = original
  switch (status) {
    case "active":
      return <Badge variant="success">Active</Badge>;
    case "paused":
      return <Badge variant="info">Paused</Badge>;
    case "pendpause":
      return <Badge variant="info">Pending pause</Badge>;
    case "cancelled":
      return <Badge variant="warning">Cancelled</Badge>;
    case "alert":
      return <Badge variant="error">Alert</Badge>;
    case "completed":
      return <Badge variant="secondary">Completed</Badge>;
    case "pendactivation":
      return <Badge variant="outline">Pending activation</Badge>;
    case "pendcancel":
      return <Badge variant="warning">Pending cancel</Badge>;
    default:
      return (
        <Badge variant="outline">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
      );
  }
};

const defaultActiveFilter = [
  {
    id: "status",
    value: ["active"],
  },
];

type PlansProps = {
  userUuid: string;
};

type TableRow = {
  name: string;
  startDate: string;
  nextCharge?: string;
  usage: string;
  status: SubscriptionStatus;
  details: string;
  isCredit: boolean;
};

export default function Plans({ userUuid }: PlansProps) {
  const app = useContext(AppContext);
  const { client } = app;
  const {
    data,
    isFetching: isLoading,
    isError,
    refetch,
  } = useUserSubscriptionsQuery(userUuid);

  const {
    data: appointmentData,
    isFetching: isLoadingAppointments,
    isError: isErrorAppointment,
    refetch: refetchAppointment,
  } = useAppointmentHistoryQuery(userUuid);

  const tableData = useMemo(
    () => convertToTableData(data, appointmentData, client.currencyIso),
    [data, appointmentData, client.currencyIso],
  );

  const [columnFilters, setColumnFilters] =
    useState<ColumnFiltersState>(defaultActiveFilter);
  const [viewMode, setViewMode] = useState<string>("active");

  // update the default filters after loading data
  useEffect(() => {
    if (tableData.length > 0) {
      const { viewMode: newViewMode, filter: newFilter } =
        getDefaultFilters(tableData);
      setColumnFilters(newFilter);
      setViewMode(newViewMode);
    }
  }, [tableData]);

  const setFilters = (status: string) => {
    switch (status) {
      case "active":
        setColumnFilters(defaultActiveFilter);
        break;
      case "all":
        setColumnFilters([]);
        break;
      case "appointments":
        setColumnFilters([
          {
            id: "isCredit",
            value: true,
          },
        ]);
        break;
      default:
        setColumnFilters(defaultActiveFilter);
        break;
    }
    setViewMode(status);
  };

  const columns = useMemo(
    () => [
      {
        header: "Plan",
        id: "name",
        cell: ({ row }) => {
          return (
            <TableCell key={row.id}>
              {row.original.name}
              <p className="tw-text-sm tw-text-neutral-600">
                {row.original.details}
              </p>
            </TableCell>
          );
        },
      },
      {
        header: "Start Date",
        accessorKey: "startDate",
      },
      {
        header: "Next Charge",
        accessorKey: "nextCharge",
      },
      {
        header: "Usage",
        accessorKey: "usage",
      },
      {
        header: "Status",
        accessorKey: "status",
        filterFn: (
          row: Row<TableRow>,
          columnId: string,
          filterValue: string[],
        ) => filterValue.includes(row.getValue(columnId)),
        cell: getStatusBadge,
      },
      // hidden column for sorting/filtering
      {
        accessorKey: "isCredit",
        cell: null,
        header: null,
      },
      // TODO accessor and action coming in future pr
      // {
      //   header: "Action",
      // },
    ],
    [],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      columnFilters,
    },
    initialState: {
      pagination: {
        pageSize: PAGINATION_PAGE_SIZE,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const dataLoading = isLoading || isLoadingAppointments;
  const dataError = isError || isErrorAppointment;
  const showPagination =
    !dataLoading && tableData.length > PAGINATION_PAGE_SIZE;
  const refetchData = () => {
    refetch();
    refetchAppointment();
  };

  return (
    <div className="tw-space-y-4">
      <SectionHeader
        title="Plans & Credits"
        actions={
          <div className="tw-flex tw-items-center tw-gap-4">
            <Button
              onClick={() =>
                window.open(
                  `/appointments/member/${userUuid}`,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              size="sm"
              variant="outline"
            >
              <Plus className="tw-mr-1 tw-h-4 tw-w-4" />
              Add Credit
            </Button>
            <Button
              onClick={() =>
                window.open(
                  `/members/edit/plan/${userUuid}`,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              size="sm"
            >
              <Plus className="tw-mr-1 tw-h-4 tw-w-4" />
              Add Plan
            </Button>
          </div>
        }
      />
      <div className="tw-rounded-lg tw-border tw-p-4 tw-bg-white tw-flex tw-flex-col tw-gap-4">
        <Tabs value={viewMode} onValueChange={(value) => setFilters(value)}>
          <TabsList className="tw-h-9">
            <TabsTrigger value="active" className="tw-text-xs">
              Active
            </TabsTrigger>
            <TabsTrigger value="all" className="tw-text-xs">
              All
            </TabsTrigger>
            <TabsTrigger value="appointments" className="tw-text-xs">
              Credits
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="tw-rounded tw-ring-1 tw-ring-black/5">
          {dataError ? (
            <ErrorState refetch={refetchData} />
          ) : dataLoading ? (
            <TableSkeleton
              numRows={PAGINATION_PAGE_SIZE}
              numCols={columns.length}
            />
          ) : (
            <Table className="tw-divide-y tw-divide-neutral-300">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id} className="tw-bg-neutral-50">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody className="tw-divide-y tw-divide-neutral-200">
                {table.getRowModel()?.rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="tw-py-0 tw-cursor-pointer"
                      onClick={() =>
                        row.original.isCredit
                          ? window.open(
                            `/appointments/member/${userUuid}`,
                            "_blank",
                            "noopener, noreferrer",
                          )
                          : window.open(
                            `/members/view/${userUuid}`,
                            "_blank",
                            "noopener, noreferrer",
                          )
                      }
                    >
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <TableCell key={cell.id} className={"tw-py-0"}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : (
                  <EmptyPlansTable
                    userUuid={userUuid}
                    numColumns={columns.length}
                    viewMode={viewMode}
                  />
                )}
              </TableBody>
            </Table>
          )}
        </div>
        {showPagination && <TablePagination table={table} />}
      </div>
    </div>
  );
}
